import React, { useState } from "react";
import { FaChevronRight, FaTimes } from "react-icons/fa";

const DEFAULT_COLORS = [
  "#667eea", "#764ba2", "#43e97b", "#fa709a",
  "#4facfe", "#30cfd0", "#f093fb", "#fda085",
  "#a18cd1", "#84fab0", "#fccb90", "#d4fc79",
];

const TOP_N = 5;

/* ── Single bar row ─────────────────────────────────────────── */
function BarRow({ label, value, max, color }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div style={{ marginBottom: 16 }}>
      {/* Label + count on the same line above the bar */}
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
        marginBottom: 8,
      }}>
        <span style={{
          fontWeight: 700, fontSize: 13,
          color: "var(--color-text)",
          letterSpacing: "-0.01em",
          whiteSpace: "nowrap", overflow: "hidden",
          textOverflow: "ellipsis",
          marginRight: 8,
        }} title={label}>
          {label}
        </span>
        <span style={{
          fontWeight: 700, fontSize: 13,
          color,
          flexShrink: 0,
        }}>
          {value.toLocaleString()}
        </span>
      </div>

      {/* Full-width track + fill */}
      <div style={{
        width: "100%", height: 9,
        background: "rgba(148,163,184,0.15)",
        borderRadius: 999,
      }}>
        <div style={{
          width: `${pct}%`,
          maxWidth: "100%",
          height: "100%",
          background: color,
          borderRadius: 999,
          transition: "width 0.6s cubic-bezier(0.4,0,0.2,1)",
          minWidth: value > 0 ? 6 : 0,
        }} />
      </div>
    </div>
  );
}

/* ── Full list drawer ───────────────────────────────────────── */
function AllEndpointsDrawer({ open, onClose, items, globalMax }) {
  if (!open) return null;
  const max = globalMax;
  return (
    <>
      <div onClick={onClose} style={{
        position: "fixed", inset: 0,
        background: "rgba(0,0,0,0.35)", zIndex: 1060,
        backdropFilter: "blur(2px)",
      }} />
      <div style={{
        position: "fixed", top: 0, right: 0, bottom: 0, width: 420,
        maxWidth: "96vw", background: "var(--color-bg-card)",
        boxShadow: "-4px 0 24px rgba(0,0,0,0.18)",
        zIndex: 1070, display: "flex", flexDirection: "column",
        borderLeft: "1px solid var(--color-border)",
      }}>
        {/* Header */}
        <div style={{
          padding: "18px 20px", flexShrink: 0,
          borderBottom: "1px solid var(--color-border)",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: 15, color: "var(--color-text)" }}>
              All Endpoint Services
            </div>
            <div style={{ fontSize: 11, color: "var(--color-text-light)", marginTop: 2 }}>
              {items.length} service{items.length !== 1 ? "s" : ""} · sorted by count
            </div>
          </div>
          <button onClick={onClose} style={{
            background: "none", border: "none", cursor: "pointer",
            color: "var(--color-text-light)", padding: 4,
          }}>
            <FaTimes size={16} />
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "20px 20px 8px" }}>
          {items.map((it, i) => (
            <BarRow key={i} label={it.label} value={it.value} max={max} color={it.color} />
          ))}
        </div>

        <div style={{
          padding: "10px 20px", borderTop: "1px solid var(--color-border)",
          fontSize: 11, color: "var(--color-text-light)", flexShrink: 0,
        }}>
          Total: {items.reduce((s, it) => s + it.value, 0).toLocaleString()} endpoints
        </div>
      </div>
    </>
  );
}

/* ── Main component ─────────────────────────────────────────── */
const EndpointDetailsChart = ({ labels = [], values = [], colors = [] }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Build sorted items
  const allItems = labels
    .map((label, i) => ({
      label,
      value: values[i] || 0,
      color: colors[i] || DEFAULT_COLORS[i % DEFAULT_COLORS.length],
    }))
    .sort((a, b) => b.value - a.value);

  // Guard — parent page handles empty/loading states; chart only renders with data
  if (allItems.length === 0) return null;

  const topItems   = allItems.slice(0, TOP_N);
  const remaining  = allItems.length - TOP_N;
  // Use a sensible max: if only one item and value is small, default to 500 so bar doesn't over-fill
  const rawMax = allItems[0].value > 0 ? allItems[0].value : 1;
  const globalMax = allItems.length === 1 && rawMax < 100 ? 500 : rawMax;

  return (
    <>
      <div style={{ width: "100%", padding: "24px 0 0" }}>
        {topItems.map((it, i) => (
          <BarRow key={i} label={it.label} value={it.value} max={globalMax} color={it.color} />
        ))}

        {remaining > 0 && (
          <div style={{ textAlign: "center", marginTop: 4 }}>
            <button
              onClick={() => setDrawerOpen(true)}
              style={{
                background: "none", border: "none", cursor: "pointer",
                display: "inline-flex", alignItems: "center", gap: 4,
                fontSize: 12, fontWeight: 600,
                color: "var(--color-primary, #667eea)",
                padding: "6px 10px", borderRadius: 6,
                transition: "background 0.15s",
              }}
              onMouseEnter={e => e.currentTarget.style.background = "rgba(102,126,234,0.08)"}
              onMouseLeave={e => e.currentTarget.style.background = "none"}
            >
              View {remaining} more <FaChevronRight size={10} />
            </button>
          </div>
        )}
      </div>

      <AllEndpointsDrawer
        open={drawerOpen}
        globalMax={globalMax}
        onClose={() => setDrawerOpen(false)}
        items={allItems}
      />
    </>
  );
};

export default EndpointDetailsChart;
